/* ═══════════════════════════════════════════════════════════════════
   COREVIAN UNIVERSITY — TIER 1 UX COMPONENTS
   ───────────────────────────────────────────────────────────────────
   PURPOSE:
   Presentation components for the Corevian Qualification Rules Engine.

   This file:
     • renders "What Do I Need to Do Next?"
     • renders qualification progress
     • renders professional empty states
     • renders error/retry states
     • renders loading skeletons

   This file DOES NOT:
     • query Supabase
     • write database records
     • determine authorization
     • determine assignment eligibility
     • duplicate qualification business rules

   Business decisions remain in:
     corevian-university-qualification-logic.js
   ═══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';


  /* ══════════════════════════════════════════════════════════════
     SAFE HTML OUTPUT
     ══════════════════════════════════════════════════════════════ */

  function esc(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[character])
    );
  }


  /* ══════════════════════════════════════════════════════════════
     PRESENTATION COLORS
     ══════════════════════════════════════════════════════════════ */

  const SEVERITY_COLOR = {
    info: '#5ED4CF',
    warning: '#FBBF24',
    urgent: '#FCA5A5',
    success: '#4ADE80'
  };


  /* ══════════════════════════════════════════════════════════════
     WHAT DO I NEED TO DO NEXT?
     ══════════════════════════════════════════════════════════════ */

  function renderNextActionPanel(container, actionResult) {
    if (!container) return;

    if (!actionResult) {
      renderErrorState(
        container,
        'Unable to determine next action.'
      );
      return;
    }

    const color =
      SEVERITY_COLOR[actionResult.severity] ||
      SEVERITY_COLOR.info;

    const isComplete =
      actionResult.severity === 'success';

    container.innerHTML = `
      <div
        class="comp-card"
        style="
          padding:20px;
          border-left:3px solid ${color};
        "
      >
        <div
          class="comp-code"
          style="color:${color}"
        >
          ${
            isComplete
              ? 'STATUS'
              : 'WHAT DO I NEED TO DO NEXT?'
          }
        </div>

        <div
          style="
            margin-top:8px;
            font-size:18px;
            font-weight:800;
            color:var(--white);
          "
        >
          ${esc(actionResult.label)}
        </div>

        <div
          class="comp-history"
          style="margin-top:6px"
        >
          ${esc(actionResult.detail)}
        </div>
      </div>
    `;
  }


  /* ══════════════════════════════════════════════════════════════
     QUALIFICATION PROGRESS
     ══════════════════════════════════════════════════════════════ */

  function renderProgressSteps(container, steps) {
    if (!container) return;

    if (!Array.isArray(steps) || !steps.length) {
      renderEmptyState(
        container,
        'No qualification progress has been recorded yet.'
      );
      return;
    }

    const stateColor = {
      complete: '#4ADE80',
      current: '#5ED4CF',
      pending: '#94A3B8'
    };

    const stateIcon = {
      complete: '✓',
      current: '●',
      pending: '○'
    };

    const items = steps.map((step, index) => {
      const nextStep =
        steps[index + 1];

      const connectorColor =
        nextStep && nextStep.state !== 'pending'
          ? stateColor[step.state]
          : 'rgba(255,255,255,.08)';

      return `
        <div
          style="
            display:flex;
            flex-direction:column;
            align-items:center;
            flex:1;
            min-width:0;
          "
        >
          <div
            style="
              width:28px;
              height:28px;
              border-radius:50%;
              display:flex;
              align-items:center;
              justify-content:center;
              font-weight:800;
              font-size:13px;
              border:2px solid ${stateColor[step.state]};
              color:${stateColor[step.state]};
              background:${
                step.state === 'complete'
                  ? 'rgba(74,222,128,.12)'
                  : 'transparent'
              };
            "
          >
            ${stateIcon[step.state]}
          </div>

          <div
            style="
              margin-top:8px;
              font-size:11px;
              font-weight:800;
              color:${
                step.state === 'pending'
                  ? 'var(--slate)'
                  : 'var(--white)'
              };
              text-align:center;
            "
          >
            ${esc(step.label)}
          </div>

          <div
            class="comp-history"
            style="
              margin-top:2px;
              text-align:center;
              font-size:10px;
            "
          >
            ${esc(step.detail)}
          </div>
        </div>

        ${
          index < steps.length - 1
            ? `
              <div
                style="
                  flex:0 0 24px;
                  height:2px;
                  background:${connectorColor};
                  margin-top:13px;
                "
              ></div>
            `
            : ''
        }
      `;
    }).join('');

    container.innerHTML = `
      <div
        class="comp-card"
        style="
          padding:20px;
          overflow-x:auto;
        "
      >
        <div
          style="
            display:flex;
            align-items:flex-start;
            min-width:620px;
          "
        >
          ${items}
        </div>
      </div>
    `;
  }


  /* ══════════════════════════════════════════════════════════════
     EMPTY STATE
     ══════════════════════════════════════════════════════════════ */

  function renderEmptyState(container, message) {
    if (!container) return;

    container.innerHTML = `
      <div class="comp-notice">
        ${esc(message || 'No records found.')}
      </div>
    `;
  }


  /* ══════════════════════════════════════════════════════════════
     ERROR STATE
     ══════════════════════════════════════════════════════════════ */

  function renderErrorState(
    container,
    message,
    onRetry
  ) {
    if (!container) return;

    const retryId =
      'cvn_retry_' +
      Math.random().toString(36).slice(2);

    container.innerHTML = `
      <div class="comp-error">

        <div>
          ${esc(
            message ||
            'Unable to load. Try again.'
          )}
        </div>

        ${
          typeof onRetry === 'function'
            ? `
              <div style="margin-top:10px">
                <button
                  type="button"
                  class="secondary-btn"
                  id="${retryId}"
                >
                  Try Again
                </button>
              </div>
            `
            : ''
        }

      </div>
    `;

    if (typeof onRetry === 'function') {
      const button =
        container.querySelector(
          '#' + retryId
        );

      if (button) {
        button.addEventListener(
          'click',
          onRetry
        );
      }
    }
  }


  /* ══════════════════════════════════════════════════════════════
     LOADING SKELETON
     ══════════════════════════════════════════════════════════════ */

  function renderSkeletonLoading(
    container,
    rows
  ) {
    if (!container) return;

    const count =
      Number(rows) > 0
        ? Number(rows)
        : 3;

    /*
      Add the animation once.
    */

    if (
      !document.getElementById(
        'cvnSkeletonStyle'
      )
    ) {
      const style =
        document.createElement('style');

      style.id =
        'cvnSkeletonStyle';

      style.textContent = `
        @keyframes cvnSkeletonPulse {
          0% {
            background-position:200% 0;
          }

          100% {
            background-position:-200% 0;
          }
        }
      `;

      document.head.appendChild(style);
    }


    function bar(width) {
      return `
        <div
          style="
            height:12px;
            border-radius:4px;
            background:
              linear-gradient(
                90deg,
                rgba(255,255,255,.06),
                rgba(255,255,255,.14),
                rgba(255,255,255,.06)
              );
            background-size:200% 100%;
            animation:
              cvnSkeletonPulse
              1.3s
              ease-in-out
              infinite;
            width:${width};
          "
        ></div>
      `;
    }


    let rowsHtml = '';

    for (
      let index = 0;
      index < count;
      index += 1
    ) {
      rowsHtml += `
        <div
          class="comp-card"
          style="
            padding:16px;
            margin-top:${
              index === 0
                ? '0'
                : '10px'
            };
          "
        >
          <div
            style="
              display:flex;
              flex-direction:column;
              gap:8px;
            "
          >
            ${bar('40%')}
            ${bar('70%')}
          </div>
        </div>
      `;
    }

    container.innerHTML =
      rowsHtml;
  }


  /* ══════════════════════════════════════════════════════════════
     AUTO HELPERS

     These helpers ask the Rules Engine to compute the answer and
     immediately render it.

     The Rules Engine must already be loaded.
     ══════════════════════════════════════════════════════════════ */

  function renderNextActionPanelAuto(
    container,
    engineInput
  ) {
    if (
      typeof window.computeNextAction !==
      'function'
    ) {
      renderErrorState(
        container,
        'Qualification Rules Engine is not loaded.'
      );
      return;
    }

    renderNextActionPanel(
      container,
      window.computeNextAction(
        engineInput
      )
    );
  }


  function renderProgressStepsAuto(
    container,
    engineInput
  ) {
    if (
      typeof window.computeQualificationProgressSteps !==
      'function'
    ) {
      renderErrorState(
        container,
        'Qualification Rules Engine is not loaded.'
      );
      return;
    }

    renderProgressSteps(
      container,
      window.computeQualificationProgressSteps(
        engineInput
      )
    );
  }

  /* =========================================================
   TIER 2 UX — ROLE-BASED DASHBOARD PANELS
   Presentation / navigation layer only.
   Does not grant, revoke, or change authorization.
   ========================================================= */

function computeRoleDashboardPanels(role) {
  const normalizedRole = String(role || '').trim().toLowerCase();

  const panelsByRole = {
    employee: [
      {
        key: 'my-training',
        title: 'My Training',
        description: 'Continue assigned Corevian University curriculum.',
        available: true
      },
      {
        key: 'my-qualification',
        title: 'My Qualification',
        description: 'Review qualification progress and readiness status.',
        available: false
      }
    ],

    trainer: [
      {
        key: 'workforce-readiness',
        title: 'Workforce Readiness',
        description: 'Review employee qualification and readiness information.',
        available: true
      },
      {
        key: 'competency-evaluation',
        title: 'Competency Evaluation',
        description: 'Document required hands-on competency evaluations.',
        available: true
      },
      {
        key: 'field-training',
        title: 'Field Training & Route Readiness',
        description: 'Document supervised field-training readiness.',
        available: true
      }
    ],

    leadership: [
      {
        key: 'workforce-readiness',
        title: 'Workforce Readiness',
        description: 'Review enterprise qualification and readiness information.',
        available: true
      },
      {
        key: 'operational-authorization',
        title: 'Operational Authorization',
        description: 'Review qualification files and record operating decisions.',
        available: true
      },
      {
        key: 'operational-endorsements',
        title: 'Operational Endorsements',
        description: 'Manage leadership-controlled service-category decisions.',
        available: true
      },
      {
        key: 'recertification',
        title: 'Recertification & Renewals',
        description: 'Review and manage controlled renewal requirements.',
        available: true
      },
      {
        key: 'exceptions-risk',
        title: 'Exceptions & Operational Risk',
        description: 'Centralized exception and operational-risk workspace.',
        available: true
      }
    ],

    admin: [
      {
        key: 'workforce-readiness',
        title: 'Workforce Readiness Command Center',
        description: 'Enterprise view of workforce qualification and readiness.',
        available: true
      },
      {
        key: 'operational-authorization',
        title: 'Operational Authorization',
        description: 'Review qualification files and final operating decisions.',
        available: true
      },
      {
        key: 'operational-endorsements',
        title: 'Operational Endorsements',
        description: 'Manage authorized service scope.',
        available: true
      },
      {
        key: 'recertification',
        title: 'Recertification & Renewals',
        description: 'Review and manage controlled renewal requirements.',
        available: true
      },
              {
        key: 'exceptions-risk',
        title: 'Exceptions & Operational Risk',
        description: 'Employees who are behind or need action — competency, renewals, documents, or authorization.',
        available: true
      },
      {
        key: 'controlled-knowledge',
        title: 'Controlled Knowledge',
        description: 'Policies, SOPs, and controlled organizational knowledge.',
        available: true
      },
      {
        key: 'executive-functions',
        title: 'Executive Functions',
        description: 'Executive-level operational and administrative functions.',
        available: true
      },
      {
        key: 'audit-compliance',
        title: 'Audit / Compliance Oversight',
        description: 'Centralized audit and compliance oversight.',
        available: true
      }
    ]
  };

  return panelsByRole[normalizedRole] || [];
}
function renderRoleDashboardPanels(container, role, opts) {
  if (!container) return;

  const options = opts || {};
  const panels = computeRoleDashboardPanels(role);

  if (!panels.length) {
    container.innerHTML = '';
    return;
  }

  const roleLabel =
    role === 'admin'
      ? 'Executive / Administration'
      : role === 'leadership'
      ? 'Executive Leadership'
      : role === 'trainer'
      ? 'Leadership'
      : 'Employee';

  /*
   * Component-owned styling.
   * Scoped entirely to #roleDashboardPanels so it cannot restyle
   * the rest of Corevian University.
   */
  const styleId = 'corevian-role-dashboard-styles';

  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;

    style.textContent = `
      #roleDashboardPanels {
        margin: 24px 0 30px;
        width: 100%;
      }

      #roleDashboardPanels .cvn-role-dashboard {
        position: relative;
        overflow: hidden;
        padding: 26px;
        border: 1px solid rgba(74, 202, 205, 0.48);
        border-radius: 18px;
        background:
          radial-gradient(
            circle at 10% 0%,
            rgba(49, 188, 191, 0.10),
            transparent 34%
          ),
          linear-gradient(
            145deg,
            rgba(9, 29, 46, 0.98),
            rgba(5, 19, 33, 0.99)
          );
        box-shadow:
          0 18px 45px rgba(0, 0, 0, 0.18),
          inset 0 1px 0 rgba(255, 255, 255, 0.025);
        color: #f7fafc;
      }

      #roleDashboardPanels .cvn-role-dashboard__heading {
        margin-bottom: 22px;
      }

      #roleDashboardPanels .cvn-role-dashboard__title-row {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 8px;
      }

      #roleDashboardPanels .cvn-role-dashboard__eyebrow {
        flex: 0 0 auto;
        color: #f4f7fa;
        font-size: 18px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      #roleDashboardPanels .cvn-role-dashboard__divider {
        width: 1px;
        height: 27px;
        background: rgba(255, 255, 255, 0.32);
      }

      #roleDashboardPanels .cvn-role-dashboard__heading h2 {
        flex: 0 0 auto;
        margin: 0;
        color: #d6ad63;
        font-size: 25px;
        line-height: 1.1;
        font-weight: 700;
      }

      #roleDashboardPanels .cvn-role-dashboard__rule {
        flex: 1 1 auto;
        height: 1px;
        min-width: 40px;
        background: linear-gradient(
          90deg,
          rgba(214, 173, 99, 0.85),
          rgba(214, 173, 99, 0.08)
        );
      }

      #roleDashboardPanels .cvn-role-dashboard__role {
        flex: 0 0 auto;
        color: #c8d5e2;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      #roleDashboardPanels .cvn-role-dashboard__heading > p {
        margin: 0;
        color: #aebfd0;
        font-size: 14px;
        line-height: 1.65;
      }

      #roleDashboardPanels .cvn-role-dashboard__grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 14px;
      }

      #roleDashboardPanels .cvn-role-dashboard__card {
        min-width: 0;
        min-height: 190px;
        display: flex;
        flex-direction: column;
        padding: 20px;
        border: 1px solid rgba(65, 190, 195, 0.34);
        border-radius: 14px;
        background:
          linear-gradient(
            145deg,
            rgba(15, 48, 67, 0.76),
            rgba(8, 27, 43, 0.92)
          );
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.025),
          0 10px 25px rgba(0, 0, 0, 0.12);
        transition:
          transform 160ms ease,
          border-color 160ms ease,
          box-shadow 160ms ease;
      }

      #roleDashboardPanels .cvn-role-dashboard__card:not(.is-unavailable):hover {
        transform: translateY(-2px);
        border-color: rgba(92, 220, 220, 0.62);
        box-shadow:
          0 14px 30px rgba(0, 0, 0, 0.18),
          0 0 20px rgba(45, 192, 194, 0.07);
      }

      #roleDashboardPanels .cvn-role-dashboard__card-body {
        flex: 1 1 auto;
      }

      #roleDashboardPanels .cvn-role-dashboard__card h3 {
        margin: 0 0 10px;
        color: #ffffff !important;
        font-size: 15px;
        line-height: 1.35;
        font-weight: 750;
      }

      #roleDashboardPanels .cvn-role-dashboard__card p {
        margin: 0;
        color: #b8c8d8 !important;
        font-size: 13px;
        line-height: 1.55;
      }

      #roleDashboardPanels .cvn-role-dashboard__card-footer {
        margin-top: 20px;
      }

      #roleDashboardPanels .cvn-role-dashboard__action {
        appearance: none;
        min-width: 104px;
        padding: 9px 18px;
        border: 1px solid #d6ad63;
        border-radius: 999px;
        background: rgba(214, 173, 99, 0.04);
        color: #ffffff;
        font: inherit;
        font-size: 12px;
        font-weight: 750;
        letter-spacing: 0.03em;
        cursor: pointer;
        transition:
          background 160ms ease,
          color 160ms ease,
          box-shadow 160ms ease,
          transform 160ms ease;
      }

      #roleDashboardPanels .cvn-role-dashboard__action:hover {
        background: #d6ad63;
        color: #071526;
        box-shadow: 0 0 18px rgba(214, 173, 99, 0.18);
        transform: translateY(-1px);
      }

      #roleDashboardPanels .cvn-role-dashboard__action:focus-visible {
        outline: 2px solid #67d6d5;
        outline-offset: 3px;
      }

      #roleDashboardPanels .cvn-role-dashboard__status {
        display: inline-flex;
        align-items: center;
        min-height: 34px;
        padding: 7px 12px;
        border: 1px solid rgba(151, 170, 188, 0.24);
        border-radius: 999px;
        color: #8193a6 !important;
        font-size: 10px;
        font-weight: 750;
        letter-spacing: 0.07em;
        text-transform: uppercase;
      }

      #roleDashboardPanels .cvn-role-dashboard__card.is-unavailable {
        border-color: rgba(109, 130, 150, 0.20);
        background: rgba(12, 31, 47, 0.54);
      }

      #roleDashboardPanels .cvn-role-dashboard__card.is-unavailable h3 {
        color: #c4cfda !important;
      }

      #roleDashboardPanels .cvn-role-dashboard__card.is-unavailable p {
        color: #8193a6 !important;
      }

      @media (max-width: 1200px) {
        #roleDashboardPanels .cvn-role-dashboard__grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media (max-width: 760px) {
        #roleDashboardPanels {
          margin: 18px 0 24px;
        }

        #roleDashboardPanels .cvn-role-dashboard {
          padding: 18px;
          border-radius: 14px;
        }

        #roleDashboardPanels .cvn-role-dashboard__title-row {
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 8px 12px;
        }

        #roleDashboardPanels .cvn-role-dashboard__divider,
        #roleDashboardPanels .cvn-role-dashboard__rule {
          display: none;
        }

        #roleDashboardPanels .cvn-role-dashboard__eyebrow {
          width: 100%;
          font-size: 14px;
        }

        #roleDashboardPanels .cvn-role-dashboard__heading h2 {
          font-size: 22px;
        }

        #roleDashboardPanels .cvn-role-dashboard__role {
          margin-left: 24px;
          font-size: 10px;
        }

        #roleDashboardPanels .cvn-role-dashboard__grid {
          grid-template-columns: 1fr;
        }

        #roleDashboardPanels .cvn-role-dashboard__card {
          min-height: 0;
          padding: 18px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  container.innerHTML = `
    <section
      class="cvn-role-dashboard"
      aria-label="Role dashboard"
    >
      <div class="cvn-role-dashboard__heading">

        <div class="cvn-role-dashboard__title-row">
          <div class="cvn-role-dashboard__eyebrow">
            ROLE WORKSPACE
          </div>

          <div
            class="cvn-role-dashboard__divider"
            aria-hidden="true"
          ></div>

          <h2>Quick Access</h2>

          <div
            class="cvn-role-dashboard__rule"
            aria-hidden="true"
          ></div>

          <div class="cvn-role-dashboard__role">
            ${roleLabel}
          </div>
        </div>

        <p>
          Role-appropriate training, qualification, and operational tools.
        </p>
      </div>

      <div class="cvn-role-dashboard__grid">
        ${panels.map(panel => {
          const isAvailable = panel.available === true;

          return `
            <div
              class="cvn-role-dashboard__card${
                isAvailable ? '' : ' is-unavailable'
              }"
              data-panel-key="${panel.key}"
            >
              <div class="cvn-role-dashboard__card-body">
                <h3>${panel.title}</h3>
                <p>${panel.description}</p>
              </div>

              <div class="cvn-role-dashboard__card-footer">
                ${
                  isAvailable
                    ? `
                      <button
                        type="button"
                        class="cvn-role-dashboard__action"
                        data-role-dashboard-action="${panel.key}"
                      >
                        Open&nbsp;&nbsp;→
                      </button>
                    `
                    : `
                      <span class="cvn-role-dashboard__status">
                        Not Yet Available
                      </span>
                    `
                }
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </section>
  `;

  container
    .querySelectorAll('[data-role-dashboard-action]')
    .forEach(button => {
      button.addEventListener('click', function () {
        const key = this.getAttribute(
          'data-role-dashboard-action'
        );

        if (typeof options.onAction === 'function') {
          options.onAction(key);
        }
      });
    });
}

  /* ══════════════════════════════════════════════════════════════
     PUBLIC PRESENTATION API
     ══════════════════════════════════════════════════════════════ */

  window.CorevianUX = {
    renderNextActionPanel,
    renderProgressSteps,
    renderEmptyState,
    renderErrorState,
    renderSkeletonLoading,
    renderNextActionPanelAuto,
    renderProgressStepsAuto,
    computeRoleDashboardPanels,
    renderRoleDashboardPanels
  };

})();